import React from "react";
import { Box, Typography, Chip, Paper, Button, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { COLORS } from "../constants";
import { safeJson, getProp } from "../helpers";
import type { SubscriptionData, ModalType, SubIssues } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  sub: SubscriptionData;
  isBundle: boolean;
  isTreat?: boolean;
  subIssues?: SubIssues;
  getVariantInfo: (variantId: string) => { productTitle: string; variantTitle: string; image?: string; tags?: string } | null;
  onOpenModal: (type: ModalType, sub: SubscriptionData) => void;
}

export function SubscriptionRow({ sub, isBundle, isTreat, subIssues, getVariantInfo, onOpenModal }: Props) {
  const dogJson = safeJson(getProp(sub, "_paws_dog_json"), null);
  const mealsJson = safeJson(getProp(sub, "_paws_meals_json"), []);
  const treatsJson = safeJson(getProp(sub, "_paws_treats_json"), {});
  const planDays = getProp(sub, "_paws_plan_days");
  const packSize = getProp(sub, "_paws_pack_size_grams");
  const hasIssues = subIssues?.hasIssues;

  return (
    <Paper
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: "12px",
        border: hasIssues ? `2px solid ${COLORS.redBorder}` : `1px solid ${COLORS.borderLight}`,
        bgcolor: hasIssues ? "#FFF8F8" : QUIZ.color.white,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
            {hasIssues && (
              <Tooltip title={subIssues?.issues.map(i => i.message).join(", ")}>
                <WarningAmberIcon sx={{ color: COLORS.red, fontSize: 18 }} />
              </Tooltip>
            )}
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.brownDeep }}>
              {sub.product_title}
            </Typography>
            <Chip
              label={sub.status}
              size="small"
              sx={{
                bgcolor: sub.status?.toLowerCase() === "active" ? COLORS.greenLight : COLORS.redLight,
                color: sub.status?.toLowerCase() === "active" ? COLORS.green : COLORS.red,
                fontWeight: 600, fontSize: 11, height: 22,
              }}
            />
          </Box>

          <Typography sx={{ fontSize: 12, color: COLORS.brown, mb: 1 }}>
            ID: {sub.id} • £{sub.price}
            {planDays && ` • ${planDays}d`}
            {packSize && ` / ${packSize}g`}
          </Typography>

          {dogJson && (
            <Typography sx={{ fontSize: 12, color: COLORS.textMuted, mb: 0.5 }}>
              <strong>Dog:</strong> {dogJson.name} ({dogJson.breed}, {dogJson.weightKg}kg)
            </Typography>
          )}

          {hasIssues && subIssues && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
              {subIssues.issues.map((issue, idx) => (
                <Chip
                  key={idx}
                  icon={<WarningAmberIcon sx={{ fontSize: 12 }} />}
                  label={issue.message}
                  size="small"
                  sx={{
                    bgcolor: issue.severity === "error" ? COLORS.redLighter : COLORS.orangeBg,
                    color: issue.severity === "error" ? COLORS.red : COLORS.orangeText,
                    fontSize: 10, height: 22,
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
              ))}
            </Box>
          )}

          {isBundle && mealsJson.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
              {mealsJson.slice(0, 4).map((meal: any, idx: number) => {
                const info = getVariantInfo(meal.variantId);
                return (
                  <Chip
                    key={idx}
                    label={`${meal.count}x ${info?.productTitle || "Recipe"}`}
                    size="small"
                    sx={{ bgcolor: COLORS.orangeLight, fontSize: 10, height: 20 }}
                  />
                );
              })}
              {mealsJson.length > 4 && (
                <Chip label={`+${mealsJson.length - 4} more`} size="small" sx={{ bgcolor: QUIZ.color.cream2, fontSize: 10, height: 20 }} />
              )}
            </Box>
          )}

          {isBundle && Object.keys(treatsJson).length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {Object.entries(treatsJson).slice(0, 3).map(([variantId, count], idx) => {
                const info = getVariantInfo(variantId);
                return (
                  <Chip
                    key={idx}
                    label={`${count}x ${info?.productTitle || "Treat"}`}
                    size="small"
                    sx={{ bgcolor: COLORS.greenLight, fontSize: 10, height: 20 }}
                  />
                );
              })}
            </Box>
          )}

          {!isBundle && (
            <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
              Quantity: {sub.quantity || 1}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {isBundle && (
            <>
              <Button size="small" variant="outlined" onClick={() => onOpenModal("meals", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
                Edit Meals
              </Button>
              <Button size="small" variant="outlined" onClick={() => onOpenModal("plan", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
                Frequency
              </Button>
              <Button size="small" variant="outlined" onClick={() => onOpenModal("dailyServing", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
                Daily Serving
              </Button>
            </>
          )}
          {!isBundle && isTreat && (
            <Button size="small" variant="outlined" onClick={() => onOpenModal("editTreat", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
              Edit Treat
            </Button>
          )}
          {!isBundle && !isTreat && (
            <Button size="small" variant="outlined" onClick={() => onOpenModal("changeQuantity", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
              Change Qty
            </Button>
          )}
          {sub.status?.toLowerCase() === "active" ? (
            <>
              <Button size="small" variant="outlined" color="error" onClick={() => onOpenModal("cancel", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
                Cancel
              </Button>
              <Button size="small" variant="outlined" onClick={() => onOpenModal("skip", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
                Skip
              </Button>
            </>
          ) : (
            <Button size="small" variant="outlined" color="success" onClick={() => onOpenModal("resume", sub)} sx={{ fontSize: 11, textTransform: "none", minWidth: 80 }}>
              Resume
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
